// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2018
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!
import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import type {
  InteractiveOptions,
  LayoutItem,
  LayoutSettings,
  UserData,
} from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import { Resolution } from '../uTheory.Notation/resolution';
import type { ScaleDegreeNoteIDQuestion } from './scaleDegreeNoteQuestionGenerator';
import {
  generateScaleDegreeNoteQuestions,
  isLabelingSolfegeSkill,
  isWritingSolfegeSkill,
  processManualScaleDegreeNoteQuestions,
  scaleDegreeNoteSkillsToAvailableModes,
} from './scaleDegreeNoteQuestionGenerator';
import type NoteHolder from '../uTheory.Notation/noteHolder';
import Music from '../uTheory.Notation/music';
import type { ScaleDegree, SolfegePresentationSystem } from './solfegeDefinitions';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';
import { makeString } from '../uTheory.Skills/skills';
import SolfegePanel from './solfegePanel/SolfegePanel.svelte';

export interface NoteToScaleDegreeIdOptions extends InteractiveOptions {
  /** Whether numbers (1, 2, #7, etc.) or names (tonic, supertonic, leading tone, etc.) is used */
  presentationSystem: SolfegePresentationSystem;
  /** Changes key after this number of question */
  changeKeyEvery?: number;
  // /** What mode are we in? */
  // mode?: ModeName | 'allMinors';
}

interface NoteToScaleDegreeIDLayoutSettings extends LayoutSettings {
  svelteDiv: LayoutItem;
}

export default class NoteToScaleDegreeID extends InteractivePrototype {
  declare options: NoteToScaleDegreeIdOptions;

  declare layout: NoteToScaleDegreeIDLayoutSettings;

  declare currentQuestion: ScaleDegreeNoteIDQuestion;

  declare questions: ScaleDegreeNoteIDQuestion[];

  protected noteHolder!: NoteHolder;

  constructor(
    displayDiv: HTMLDivElement,
    options: NoteToScaleDegreeIdOptions,
    userData?: UserData
  ) {
    options = { ...options };
    super(displayDiv, options, userData);
    this.layout = {
      instructions: {
        name: 'instructions',
        percent: 15,
      },
      notation: {
        name: 'notation',
        percent: 40,
      },
      svelteDiv: {
        div: this.svelteDiv,
        visible: true,
        percent: 60,
      },
    };

    if (
      this.options.questions &&
      this.options.questions.some((thing) => !['string', 'number'].includes(typeof thing))
    ) {
      const invalidQuestion = this.options.questions.find(
        (q) => !['string', 'number'].includes(typeof q)
      );
      throw new Error(
        `Invalid question format detected in \`this.options.questions\`: ${typeof invalidQuestion}`
      );
    }

    // TODO GCR when skill object exists swap comments on next two lines
    // this.targetSpeedFromSkills = NoteToScaleDegreeID.registry.skills;
    this.targetSpeed = 5;
    this.init(displayDiv, options, userData);
  }

  private solfegeSystem!: SolfegeSystemName;

  private availableModes: (ModeName | 'allMinors')[] = [];

  init(displayDiv: HTMLDivElement, options: NoteToScaleDegreeIdOptions, userData?: UserData) {
    options.maxQuestions = options.maxQuestions || 10;

    if (options.drill && !options.skills) {
      throw Error('noteToScaleDegreeID requires skills array when run in drill mode.');
    }

    if (options.drill && options.skills) {
      const skills = options.skills.map(makeString);
      this.availableModes = scaleDegreeNoteSkillsToAvailableModes(skills);
    }

    // Set whether this interactive uses degree numbers or names
    if (!options.presentationSystem) {
      const useNames = options.skills?.map(makeString).includes('scale_degree_names');
      // We use the student's solfege system if we're training a solfege skill
      const useStudentSolfegeSystem = !!options.skills
        ?.map((skill) => makeString(skill))
        .filter((t): t is string => !!t)
        .find((skill) => isWritingSolfegeSkill(skill) || isLabelingSolfegeSkill(skill));
      const studentSolfegeSystem = userData?.userStore.preferences.solfegeMethod || 'scaleDegrees';

      if (useNames) options.presentationSystem = 'names';
      else if (useStudentSolfegeSystem) {
        options.presentationSystem =
          studentSolfegeSystem === 'scaleDegrees' ? 'scaleDegrees' : 'solfege';
      } else {
        options.presentationSystem = 'scaleDegrees';
      }
    }

    this.solfegeSystem = userData?.userStore.preferences.solfegeMethod || 'solfegeDoMinor';

    this.settings = {
      instructions: true,
      notation: {
        fixedWidth: 120,
        ariaLive: 'alert',
        ariaTitle: () => {
          if (!this.currentQuestion) return '';
          return `
            ${this.currentQuestion.key.quality} key with ${this.currentQuestion.key.toAriaString()}.
            Note: ${this.currentQuestion.note.toAriaString({ octave: false })}
          `;
        },
      },
      checkMark: true,
      player: {},
      progressTracker: true,
    };

    this.questionPrompt = (text: string[]) => {
      const testMode = this.optionsTrue('testMode');
      let questionPrompt;

      // ${} instead of {question} as this interactive shares question generator with scaleDegreeToNoteID
      // but require different texts
      questionPrompt = `Identify this scale degree in ${this.currentQuestion.key.quality}:`;

      if (!testMode) {
        questionPrompt += `<br />#${text[2]} of ${this.questions.length}`;
      }

      return questionPrompt;
    };

    return super.init(displayDiv, options, userData);
  }

  svelteDiv!: HTMLDivElement;

  solfegePanel!: SolfegePanel;

  createCustomDivs() {
    this.svelteDiv = document.createElement('div');
    this.solfegePanel = new SolfegePanel({
      target: this.svelteDiv,
      props: {
        presentationSystem: this.options.presentationSystem,
        solfegeSystem: this.solfegeSystem,
        mode: 'Major',
        callback: (sd: '' | ScaleDegree) => this.checkAnswer(sd),
      },
    });

    this.layout.svelteDiv = {
      div: this.svelteDiv,
      visible: true,
      percent: 60,
      callback: this.solfegePanel.resize,
    };

    this.displayDiv.appendChild(this.svelteDiv);
  }

  initializeMusic() {
    this.music = Music.fromString(`key:CM
      clef:treble */w |||`);
    if (!this.music.voices[0]?.measures[0]?.noteHolders?.[0]) {
      throw new Error(`Noteholder was not initialized successfully.`);
    }
    this.noteHolder = this.music.voices[0].measures[0].noteHolders[0];
  }

  generateQuestions(
    questions: (string | number)[] = this.options.questions as (string | number)[]
  ): ScaleDegreeNoteIDQuestion[] {
    const { presentationSystem } = this.options;
    const solfegeMethod = this.userData?.userStore?.preferences.solfegeMethod || 'solfegeDoMinor';
    this.questions = this.options.drill
      ? generateScaleDegreeNoteQuestions(
          this.options.maxQuestions,
          this.availableModes,
          presentationSystem,
          solfegeMethod,
          this.options.changeKeyEvery,
          'label'
        )
      : processManualScaleDegreeNoteQuestions(
          questions,
          presentationSystem,
          solfegeMethod,
          'label'
        );

    return this.questions;
  }

  newQuestion(disableAudio?: any) {
    super.newQuestion(disableAudio);
    this.solfegePanel.$set({
      mode: this.currentQuestion.mode,
    });
    // Rendering the new note onto the staff
    const currentNote = this.currentQuestion.note;
    this.music.staves[0]!.setClef(this.currentQuestion.clef);
    this.music.setKeySignature(this.currentQuestion.key);
    this.noteHolder.clearNotes();
    this.noteHolder.push(currentNote);

    // Resize based on number of accidentals in key signature
    const accidentalOffset = this.currentQuestion.key.quality === 'minor' ? 15 : 0;
    const width = 10 * Math.abs(this.currentQuestion.key.accidentalsCount) + 85 + accidentalOffset;
    this.notation.options.fixedWidth = width;

    this.notation.render();

    this.resize();
  }

  checkAnswer(studentAnswer: ScaleDegree | '' = '') {
    if (studentAnswer === '8') {
      studentAnswer = '1';
    }
    if (this.rightAnswerDelay) return undefined;
    studentAnswer = `${studentAnswer}`;
    const expectedAnswer: ScaleDegree =
      this.currentQuestion.degree === '8' ? '1' : this.currentQuestion.degree;
    const correct = studentAnswer === expectedAnswer;

    if (this.options.testMode) {
      return this.checkpointAnswer({ correct, studentAnswer, expectedAnswer });
    }

    // When correct, call rightAnswer();
    if (correct) {
      this.solfegePanel.$set({
        disabled: true,
      });
      setTimeout(() => {
        this.solfegePanel.$set({
          disabled: false,
        });
      }, 2000);
      return this.rightAnswer(2000);
    }
    // When incorrect, call wrongAnswer();
    return this.wrongAnswer();
  }

  playAnswer() {
    const resolver = new Resolution(this.player!);
    const noteToPlay = this.currentQuestion.note.clone();

    resolver.resolve(this.currentQuestion.key, noteToPlay, { tempo: 100 });
  }

  newCheckpointQuestion(questionString: string | number) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.questionNumber = 0;
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  getAnswerAndData(answerObj: { studentAnswer: string; correctAnswer: string; correct: boolean }) {
    const answerAndData = {
      question: `${this.currentQuestion.key}: ${this.currentQuestion.degree}`,
      questionType: 'noteToScaleDegreeID',
      questionText: this.currentInstructions!,
      questionSVG: this.notation.exportSVG(),
      studentAnswerText: answerObj.studentAnswer || '__',
      studentAnswerSVG: undefined,
      correctAnswerText: answerObj.correctAnswer,
      rightAnswers: answerObj.correct ? 1 : 0,
      wrongAnswers: answerObj.correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };

    return answerAndData;
  }

  gameOver() {
    return super.gameOver();
  }

  exit() {
    this.solfegePanel.$destroy();
    super.exit();
  }

  static registry: InteractiveRegistryEntry[] = [
    {
      description: 'Spell Scale Degrees',
      // only have scale degree names if included in skills
      skills: [
        'scale_degree_conversion_major',
        'scale_degree_conversion_minor',
        'scale_degree_names',
      ],
    },
    {
      description: 'Spell Scale Degrees/Solfege',
      skills: [
        'labeling_solfege_major',
        'labeling_solfege_natural_minor',
        'labeling_solfege_all_minors',
      ],
    },
  ];
}
